import { CheckCircleOutline, Download, ErrorOutlineOutlined, PendingOutlined } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import { IconButton, TableCell, TableRow, Tooltip } from '@mui/material';
import { FC, ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import API from 'api/API';
import { BulkUploadType, CheckStatus, CheckStatusMapping, UploadTypeMapping } from 'api/Responses/BulkUploadResponses';
import { useLanguage } from 'context/AppContext';
import { formatDate } from 'utils/helpers/DateTimeFormat.helpers';
import { getDownloadUrlFromGS } from 'utils/helpers/googleStorage.helpers';
import { isActionRequired } from '../DataUpload.helpers';
import * as Styles from './DataUploadListItem.styles';

interface DataUploadListItemParams {
  file: BulkUploadType;
}

const DataUploadListItem: FC<DataUploadListItemParams> = ({ file }) => {
  const [language] = useLanguage();
  const navigate = useNavigate();

  const fileStatus = Object.values(CheckStatus).find(filter => CheckStatusMapping[filter]?.includes(file.status));

  const handleClickTableRow = () => {
    if (isActionRequired(file)) {
      navigate(`/data-upload/merge/${file.upload_type}/${file.upload_id}`);
    }
  };

  const fileStatusIcon = (): ReactElement => {
    switch (fileStatus) {
      case CheckStatus.UPLOAD_CHECK_STARTED:
      case CheckStatus.DRAFT_UPLOAD_STARTED:
        return <RotateLeftIcon color="success" />;
      case CheckStatus.UPLOAD_CHECK_COMPLETE:
        return <PendingOutlined color="warning" />;
      case CheckStatus.UPLOAD_CHECK_FAILED:
      case CheckStatus.DRAFT_UPLOAD_FAILED:
        return <ErrorOutlineOutlined color="error" />;
      case CheckStatus.UPLOAD_COMPLETE:
      case CheckStatus.DRAFT_UPLOAD_COMPLETE:
        return <CheckCircleIcon color="success" />;
      case CheckStatus.DRAFT_UPLOAD_PARTIAL_SUCCESS:
        return <CheckCircleOutline color="warning" />;
      default:
        return <DoNotDisturbOnOutlinedIcon color="disabled" />;
    }
  };

  const handleClickDownload = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    try {
      e.stopPropagation();
      const link = document.createElement('a');
      const downloadUrl = await getDownloadUrlFromGS(file.uploaded_file_location);
      link.href = downloadUrl;
      link.download = file.filename;
      link.click();
    } catch (e) {
      API.errorHandler(e);
    }
  };

  return (
    <TableRow hover onClick={handleClickTableRow}>
      <TableCell>
        {fileStatus && (
          <Styles.FileUploadListItemIconContainer>
            {fileStatusIcon()}
            {fileStatus}
          </Styles.FileUploadListItemIconContainer>
        )}
      </TableCell>
      <TableCell>{file.filename}</TableCell>
      <TableCell>{UploadTypeMapping[file.upload_type]}</TableCell>
      <TableCell>{file.created_by}</TableCell>
      <TableCell>{formatDate(file.created_at, true)}</TableCell>
      <TableCell>
        {file.total_uploaded_items === null ? (
          <Tooltip title={language.DataUpload.table.nilToolTip}>
            <div>{language.DataUpload.table.nil}</div>
          </Tooltip>
        ) : (
          <div>{file.total_uploaded_items}</div>
        )}
      </TableCell>
      <TableCell>
        <IconButton onClick={handleClickDownload}>
          <Download />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

export default DataUploadListItem;
